import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../styles/fontStyles';
import { datasetFilterConfig, DatasetKey } from '../configs/datasetConfig';
import TooltipFromDB from '../components/common/TooltipFromDB';
import { useThemeContext } from '../contexts/ThemeContext';
import { CompoundMetaData } from '../types/reports';
import CompoundFilterSelect from './details/CompoundFilterSelect';
import FilterbyGenderButton from './FilterByGenderButton';

type ComparisonSelectorProps = {
  onDatasetChange: (dataset: DatasetKey, filter: string) => void;
  showCompoundDropdownFilter?: boolean;
  compoundMetaData?: Record<string, CompoundMetaData>;
  setSelectedCompound?: (compound: string) => void;
  showDemographicFilters?: boolean;
  selectedCompound?: string;
  compoundFullName?: string;
};

const ComparisonSelector: React.FC<ComparisonSelectorProps> = ({
  setSelectedCompound,
  compoundMetaData,
  onDatasetChange,
  showCompoundDropdownFilter = false,
  showDemographicFilters = true,
  selectedCompound,
  compoundFullName,
}) => {
  const [selectedDataset, setSelectedDataset] = useState<DatasetKey>('NHANES');
  const [selectedFilter, setSelectedFilter] = useState<string>('All');

  const { theme } = useThemeContext();

  const handleDatasetChange = (dataset: DatasetKey) => {
    setSelectedDataset(dataset);
    const defaultFilter = datasetFilterConfig[dataset].defaultFilter;
    setSelectedFilter(defaultFilter);
    onDatasetChange(dataset, defaultFilter);
  };

  const handleFilterToggle = (filter: string) => {
    const newFilter = selectedFilter === filter ? 'All' : filter;
    setSelectedFilter(newFilter);
    onDatasetChange(selectedDataset, newFilter);
  };

  const isFilterSelected = (filter: string) => selectedFilter === filter;

  const showCompoundDropdown = () => {
    // Some of these props are mandatory, so we can't render the dropdown without them
    if (!showCompoundDropdownFilter || !compoundMetaData || !setSelectedCompound) return <></>;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: showDemographicFilters ? '100%' : 'auto',
          marginLeft: 'auto',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          sx={{
            color: theme.custom.report.timeline.comparisonSelector.typography.selectCompound.color,
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {/* <Typography sx={{ color: colors.m3SysDarkPrimary, fontSize: '14px', fontWeight: 500 }}> */}
          Select compound:
        </Typography>
        <CompoundFilterSelect
          compoundMetaData={compoundMetaData}
          setSelectedChartCompound={setSelectedCompound}
          selectedCompound={selectedCompound}
        />
      </Box>
    );
  };

  const renderFilterTextAndButtons = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'column', sm: 'row', md: 'column', smMd800: 'column' },
          alignItems: { xs: 'flex-start', lg: 'left', sm: 'center' },
          gap: { xs: '16px', sm: '32px', lg: '10px', md: '10px', smMd800: '10px' },
          marginLeft: { xs: 0, smMd800: 'auto' },
        }}
      >
        {/* commenting this as we have only one dataset currently and later on when we ahve more datasets, we can use this section to add filter */}
        {/* Dataset Section */}
        {/* <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              color: colors.m3SysDarkPrimary,
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Datasets:
          </Typography>
          <BorderedButton
            sxProps={{
              color: selectedDataset === 'NHANES' ? colors.blue900 : colors.m3SysDarkPrimary,
              fontSize: '14px',
              fontWeight: 700,
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
              height: '32px',
            }}
            additionalProps={{
              style: { borderRadius: '8px', height: '32px' },
              startIcon: (
                <img src={'/icons/check_small.svg'} alt={'Activate test'} style={{ width: '18px', height: '18px' }} />
              ),
            }}
            onClick={() => handleDatasetChange('NHANES')}
            buttonText="NHANES"
            variant={selectedDataset === 'NHANES' ? 'contained' : 'outlined'}
          />
        </Box> */}
        {/* compound dropdown */}
        {showCompoundDropdown()}
        {/* Filter Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ color: theme.custom.report.timeline.filterByGender.label.color, fontSize: '14px', fontWeight: 500 }}>
            {selectedDataset === 'NHANES' ? 'Filter by gender:' : 'Filter by:'}
          </Typography>
          {datasetFilterConfig[selectedDataset].filters
            .filter((filter) => filter !== 'All')
            .map((filter) => (
              <FilterbyGenderButton
                filter={filter}
                isFilterSelected={isFilterSelected}
                handleFilterToggle={handleFilterToggle}
              />
            ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.custom.report.timeline.comparisonSelector.backgroundColor,
        borderRadius: '12px',
        display: 'flex',
        flexDirection: { xs: 'column', smMd800: 'row' },
        alignItems: { xs: 'flex-start', smMd800: 'center', md: 'center' },
        justifyContent: 'space-between',
        gap: '16px',
      }}
    >
      {showDemographicFilters ? (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: '5px', alignItems: 'flex-start' }}>
              <Typography
                sx={{
                  color: theme.custom.report.timeline.comparisonSelector.typography.compareResults.color,
                  fontSize: '22px',
                  fontWeight: 400,
                  lineHeight: '28px',
                }}
              >
                Compare your results against {selectedDataset}
              </Typography>
              <TooltipFromDB
                helpId="nhanes-desc"
                imageSxProps={{ mt: '4px' }}
                additionalCircularSxProps={{ width: '20px !important', height: '20px !important' }}
              />
            </Box>
            <Typography
              sx={{
                display: { xs: 'none', sm: 'block' },
                color: theme.custom.report.timeline.comparisonSelector.typography.lowerThanYourResults.color,
                fontSize: '12px',
                fontWeight: 400,
                maxWidth: '400px',
                whiteSpace: 'normal',
              }}
            >
              {!showCompoundDropdownFilter && (
                <>
                  This graph shows the percentage of the population with a NASEM level lower than that shown on the
                  bottom axis.
                </>
              )}
              {/* This ensures the following text is shown only in Nasem level timeline and not in the compounds timeline */}
              {selectedCompound === '' && (
                <>This graph shows the percentage of the population with a NASEM level lower than your results.</>
              )}
            </Typography>
            {selectedCompound && (
              <Typography
                sx={{
                  color: theme.custom.report.timeline.comparisonSelector.typography.compoundFullname.color,
                  fontSize: '22px',
                  fontWeight: 400,
                  marginTop: '16px',
                }}
              >
                {compoundFullName}
              </Typography>
            )}
          </Box>
          {renderFilterTextAndButtons()}
          <Typography
            sx={{
              display: { xs: 'block', sm: 'none' },
              color: theme.custom.report.timeline.comparisonSelector.typography.populationBelowValue.color,
              fontSize: '12px',
              fontWeight: 400,
              marginBottom: '8px',
            }}
          >
            % of population with scores below the indicated value
          </Typography>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '16px',
              flex: 1,
              marginBottom: '32px',
            }}
          >
            <Box sx={{ display: 'flex', gap: '5px', alignItems: 'flex-start' }}>
              <Typography
                sx={{
                  color: theme.custom.report.timeline.comparisonSelector.typography.compoundNotInNhanes.color,
                  fontSize: '22px',
                  fontWeight: 400,
                  lineHeight: '28px',
                }}
              >
                This compound is not in the NHANES studies
              </Typography>
              <TooltipFromDB
                helpId="nhanes-desc"
                imageSxProps={{ mt: '6px' }}
                additionalCircularSxProps={{ width: '20px !important', height: '20px !important' }}
              />
            </Box>
            <Typography
              sx={{
                color: theme.custom.report.timeline.comparisonSelector.typography.compoundFullname.color,
                fontSize: '22px',
                fontWeight: 400,
              }}
            >
              {compoundFullName}
            </Typography>
          </Box>
          {showCompoundDropdown()}
        </Box>
      )}
    </Box>
  );
};

export default ComparisonSelector;
